<template>
  <div class="comparison-page">
    <!-- 头部 -->
    <div class="header">
      <button class="btn-back" @click="goBack">← 返回</button>
      <div class="user-info">
        <span class="username">👤 {{ username }}</span>
        <RoleSelector @role-changed="handleRoleChanged" />
        <button class="btn-logout" @click="logout">退出登录</button>
      </div>
      <h1>🆚 多车型对比分析</h1>
      <p>基于真实用户口碑的全方位对比（最多支持5个车型）</p>
    </div>

    <!-- 选择器区域 - 全新简洁设计 -->
    <div class="selector-wrapper">
      <div class="selector-container" :class="`cars-${comparisonCars.length}`">
        <div 
          v-for="(car, index) in comparisonCars" 
          :key="car.id" 
          class="car-selector"
        >
          <div class="car-label" :style="{ background: getCarColor(index) }">
            {{ String.fromCharCode(65 + index) }}
          </div>
          
          <div class="car-inputs">
            <div class="input-wrapper">
              <div class="searchable-select">
                <input 
                  type="text" 
                  v-model="car.seriesSearch" 
                  @focus="car.seriesDropdown = true"
                  @blur="hideDropdown(index, 'series')"
                  @input="filterSeries(car)"
                  :placeholder="car.series || '选择车系'"
                  class="select-input"
                />
                <ul v-show="car.seriesDropdown && car.filteredSeries.length > 0" class="dropdown-list">
                  <li @click="selectSeries(car, '')" class="dropdown-item">全部</li>
                  <li 
                    v-for="series in car.filteredSeries" 
                    :key="series.value" 
                    @click="selectSeries(car, series.value)"
                    class="dropdown-item"
                  >
                    {{ series.value }} ({{ series.count }})
                  </li>
                </ul>
              </div>
            </div>
            
            <div class="input-wrapper">
              <div class="searchable-select">
                <input 
                  type="text" 
                  v-model="car.modelSearch" 
                  @focus="car.modelDropdown = true"
                  @blur="hideDropdown(index, 'model')"
                  @input="filterModels(car)"
                  :disabled="!car.series"
                  :placeholder="car.model || '选择车型'"
                  class="select-input"
                />
                <ul v-show="car.modelDropdown && car.filteredModels.length > 0" class="dropdown-list">
                  <li @click="selectModel(car, '')" class="dropdown-item">全部</li>
                  <li 
                    v-for="model in car.filteredModels" 
                    :key="model.value" 
                    @click="selectModel(car, model.value)"
                    class="dropdown-item"
                  >
                    {{ model.value }} ({{ model.count }})
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <button 
            v-if="comparisonCars.length > 2" 
            class="btn-remove-car" 
            @click="removeCar(index)"
          >
            ×
          </button>
        </div>

        <!-- VS 图标（2车时显示） -->
        <div v-if="comparisonCars.length === 2" class="vs-icon">VS</div>

        <!-- 添加车型按钮 -->
        <button 
          v-if="comparisonCars.length < 5" 
          class="btn-add-car" 
          @click="addCar"
        >
          <span class="add-plus">+</span>
          <span class="add-label">添加车型</span>
        </button>
      </div>
      
      <!-- 开始对比按钮 -->
      <button 
      style="width: 100%;color: dodgerblue;height: 40px;border-radius: 10px;border: 1px solid dodgerblue;background: white;cursor: pointer;"
        @click="loadComparisonData" 
        :disabled="!canCompare"
      >
        开始对比 ({{ validCarsCount }})
      </button>
    </div>

    <!-- 对比结果区域 -->
    <div v-if="showResults" class="comparison-results">
      
      <!-- 2-3车对比：使用左右对称布局 -->
      <template v-if="comparisonResults.length <= 3">
        
        <!-- 核心指标对比 - 对称布局 -->
        <div class="section-title">📊 核心指标对比</div>
        <div class="metrics-symmetric">
          <div class="metric-row" v-for="(metric, idx) in symmetricMetrics" :key="idx">
            <div 
              v-for="(car, index) in comparisonResults" 
              :key="car.id"
              class="metric-cell"
              :class="{ 
                'winner': isWinner(metric.key, car, metric.reverse),
                [`car-${index}`]: true
              }"
              :style="{ borderTopColor: getCarColor(index) }"
            >
              <div class="car-name">{{ car.displayName }}</div>
              <div class="metric-value">{{ getMetricValue(car, metric.key) }}</div>
              <div class="metric-label">{{ metric.label }}</div>
            </div>
            
            <!-- 指标名称（居中） -->
            <div class="metric-name-center">
              <span class="metric-icon">{{ metric.icon }}</span>
              <span>{{ metric.label }}</span>
            </div>
          </div>
        </div>

        <!-- 情感分布对比 - 并排展示 -->
        <div class="section-title">🎭 情感分布对比</div>
        <div class="charts-row">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="chart-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="chart-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            <div :ref="el => { if (el) chartRefs.sentiment[car.id] = el }" class="chart"></div>
          </div>
        </div>

        <!-- 话题热度对比 - 并排展示 -->
        <div class="section-title">🔍 话题热度对比</div>
        <div class="charts-row">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="chart-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="chart-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            <div :ref="el => { if (el) chartRefs.topic[car.id] = el }" class="chart-large"></div>
          </div>
        </div>

        <!-- 优劣势对比 - 并排展示 -->
        <div class="section-title">⚖️ 优劣势对比</div>
        <div class="advantages-row">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="advantage-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="advantage-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            
            <div class="advantage-section">
              <h4 class="advantage-title positive-title">
                <span class="title-icon">✓</span>
                <span>核心优势</span>
                <span class="title-count">(Top 5)</span>
              </h4>
              <div class="advantage-list">
                <div v-for="(item, idx) in car.advantages" :key="idx" class="advantage-item positive">
                  <span class="item-rank">{{ idx + 1 }}</span>
                  <span class="item-text">{{ item.aspect }}</span>
                  <span class="item-count">{{ item.count }}</span>
                </div>
                <div v-if="car.advantages.length === 0" class="empty-hint">暂无明显优势</div>
              </div>
            </div>
            
            <div class="advantage-section">
              <h4 class="advantage-title negative-title">
                <span class="title-icon">✗</span>
                <span>待改进项</span>
                <span class="title-count">(Top 5)</span>
              </h4>
              <div class="advantage-list">
                <div v-for="(item, idx) in car.disadvantages" :key="idx" class="advantage-item negative">
                  <span class="item-rank">{{ idx + 1 }}</span>
                  <span class="item-text">{{ item.aspect }}</span>
                  <span class="item-count">{{ item.count }}</span>
                </div>
                <div v-if="car.disadvantages.length === 0" class="empty-hint">暂无明显劣势</div>
              </div>
            </div>
          </div>
        </div>

      </template>

      <!-- 4-5车对比：使用网格布局 -->
      <template v-else>
        
        <!-- 核心指标对比表格 -->
        <div class="section-title">📊 核心指标对比</div>
        <div class="metrics-table-wrapper">
          <table class="metrics-table">
            <thead>
              <tr>
                <th class="metric-name-col">指标</th>
                <th 
                  v-for="(car, index) in comparisonResults" 
                  :key="car.id"
                  :style="{ background: getCarColor(index), color: 'white' }"
                >
                  {{ car.displayName }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="metric in tableMetrics" :key="metric.key">
                <td class="metric-name-col">
                  <span class="metric-icon">{{ metric.icon }}</span>
                  {{ metric.label }}
                </td>
                <td 
                  v-for="(car, index) in comparisonResults" 
                  :key="car.id"
                  :class="{ 'best': isWinner(metric.key, car, metric.reverse) }"
                >
                  <div class="metric-value">{{ getMetricValue(car, metric.key) }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 情感分布对比 -->
        <div class="section-title">🎭 情感分布对比</div>
        <div class="charts-grid">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="chart-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="chart-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            <div :ref="el => { if (el) chartRefs.sentiment[car.id] = el }" class="chart"></div>
          </div>
        </div>

        <!-- 话题热度对比 -->
        <div class="section-title">🔍 话题热度对比</div>
        <div class="charts-grid">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="chart-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="chart-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            <div :ref="el => { if (el) chartRefs.topic[car.id] = el }" class="chart-large"></div>
          </div>
        </div>

        <!-- 优劣势对比 -->
        <div class="section-title">⚖️ 优劣势对比</div>
        <div class="advantages-grid">
          <div 
            v-for="(car, index) in comparisonResults" 
            :key="car.id" 
            class="advantage-card"
            :style="{ borderTopColor: getCarColor(index) }"
          >
            <div class="advantage-header">
              <span class="car-badge" :style="{ background: getCarColor(index) }">
                {{ String.fromCharCode(65 + index) }}
              </span>
              <h3>{{ car.displayName }}</h3>
            </div>
            
            <div class="advantage-section">
              <h4 class="advantage-title positive-title">
                <span class="title-icon">✓</span>
                <span>核心优势</span>
              </h4>
              <div class="advantage-list">
                <div v-for="(item, idx) in car.advantages" :key="idx" class="advantage-item positive">
                  <span class="item-rank">{{ idx + 1 }}</span>
                  <span class="item-text">{{ item.aspect }}</span>
                  <span class="item-count">{{ item.count }}</span>
                </div>
                <div v-if="car.advantages.length === 0" class="empty-hint">暂无数据</div>
              </div>
            </div>
            
            <div class="advantage-section">
              <h4 class="advantage-title negative-title">
                <span class="title-icon">✗</span>
                <span>待改进项</span>
              </h4>
              <div class="advantage-list">
                <div v-for="(item, idx) in car.disadvantages" :key="idx" class="advantage-item negative">
                  <span class="item-rank">{{ idx + 1 }}</span>
                  <span class="item-text">{{ item.aspect }}</span>
                  <span class="item-count">{{ item.count }}</span>
                </div>
                <div v-if="car.disadvantages.length === 0" class="empty-hint">暂无数据</div>
              </div>
            </div>
          </div>
        </div>

      </template>

    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state">
      <div class="empty-icon">🔍</div>
      <div class="empty-text">请至少选择两个车型进行对比</div>
      <div class="empty-hint">选择车系和车型后，点击"开始对比"按钮</div>
    </div>

    <!-- AI图标（最小化状态） -->
    <div v-if="aiIconMinimized && !showAIAnalysis" class="ai-icon-minimized" @click="openAIAnalysis">
      <span class="icon">🤖</span>
      <span class="pulse-ring"></span>
    </div>

    <!-- 快捷助手提示 -->
    <div class="assistant-hint" @click="toggleQuickPanel">
      <span class="hint-icon">✨</span>
      <span class="hint-text">双击 <span class="kbd">空格</span> 唤醒口碑智能助手</span>
    </div>

    <!-- 快捷面板 -->
    <div v-if="showQuickPanel" class="quick-panel-overlay" @click.self="toggleQuickPanel">
      <transition name="slide-up">
        <div class="quick-panel">
          <iframe 
            src="https://chatkb.gausscode.com/chat/share?shareId=nfkhdg0g926tk1njsgttd76p" 
            frameborder="0" 
            allow="*"
          ></iframe>
        </div>
      </transition>
    </div>

    <!-- AI分析弹窗 -->
    <transition name="fade-slide">
      <div v-if="showAIAnalysis" class="ai-analysis-popup">
        <div class="ai-popup-header">
          <div class="ai-popup-title">AI智能解读</div>
          <button class="ai-popup-close" @click="closeAIAnalysis">×</button>
        </div>
        <div class="ai-popup-body">
          <div v-if="aiAnalyzing" class="ai-loading">
            <div class="ai-loading-dots">
              <span></span><span></span><span></span>
            </div>
            <p>AI正在分析数据中...</p>
          </div>
          <div v-else class="ai-content">
            <div class="md-content" v-html="renderedHtml"></div>
            <span v-if="aiStreaming" class="cursor-blink">▊</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import dongchediApi from '../api/dongchediApi'
import aiApi from '../api/aiApi'
import RoleSelector from '../components/RoleSelector.vue'
import { renderMarkdown } from '../utils/markdown'

export default {
  name: 'MultiComparison',
  components: {
    RoleSelector
  },
  data() {
    return {
      username: localStorage.getItem('username') || 'Admin',
      currentRole: localStorage.getItem('userRole') || '',
      // 车型列表
      comparisonCars: [
        this.createEmptyCar(1),
        this.createEmptyCar(2)
      ],
      nextCarId: 3,
      
      // 下拉选项数据
      allCarNames: [],
      seriesList: [],
      
      // 对比结果
      showResults: false,
      comparisonResults: [],
      
      // 图表引用
      chartRefs: {
        sentiment: {},
        topic: {}
      },
      chartInstances: {
        sentiment: {},
        topic: {}
      },
      
      // AI分析
      showAIAnalysis: false,
      aiAnalyzing: false,
      aiStreaming: false,
      aiAnalysisText: '',
      displayedText: '',
      renderedHtml: '',
      aiIconMinimized: false,
      
      // 快捷助手
      showQuickPanel: false,
      spaceCount: 0,
      spaceTimer: null,
      
      // 颜色方案
      carColors: [
        '#5b6df2',
        '#a855f7',
        '#22c55e',
        '#f97316',
        '#ec4899'
      ],
      
      // 对称布局指标配置
      symmetricMetrics: [
        { key: 'total_count', label: '评论总数', icon: '💬', reverse: false },
        { key: 'positiveRate', label: '正面率', icon: '😊', reverse: false },
        { key: 'avg_score', label: '平均情感分', icon: '⭐', reverse: false },
        { key: 'negative_count', label: '负面评论', icon: '😟', reverse: true }
      ],
      
      // 表格布局指标配置
      tableMetrics: [
        { key: 'total_count', label: '评论总数', icon: '💬', reverse: false },
        { key: 'positiveRate', label: '正面率', icon: '😊', reverse: false },
        { key: 'avg_score', label: '平均情感分', icon: '⭐', reverse: false },
        { key: 'positive_count', label: '正面评论', icon: '👍', reverse: false },
        { key: 'negative_count', label: '负面评论', icon: '😟', reverse: true }
      ]
    }
  },
  computed: {
    canCompare() {
      return this.validCarsCount >= 2
    },
    validCarsCount() {
      return this.comparisonCars.filter(car => car.series || car.model).length
    }
  },
  async mounted() {
    console.log('对比页面加载，初始车型数量:', this.comparisonCars.length)
    await this.preloadData()
    console.log('数据预加载完成')
    this.initKeyboardShortcut()
  },
  beforeUnmount() {
    this.destroyAllCharts()
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    // ===== 车型管理 =====
    createEmptyCar(id) {
      return {
        id: id || this.nextCarId++,
        series: '',
        model: '',
        seriesSearch: '',
        modelSearch: '',
        seriesDropdown: false,
        modelDropdown: false,
        filteredSeries: [],
        filteredModels: [],
        modelsList: []
      }
    },
    
    addCar() {
      if (this.comparisonCars.length < 5) {
        const newCar = this.createEmptyCar()
        newCar.filteredSeries = [...this.seriesList]
        newCar.filteredModels = []
        this.comparisonCars.push(newCar)
      }
    },
    
    removeCar(index) {
      if (this.comparisonCars.length > 2) {
        this.comparisonCars.splice(index, 1)
        if (this.showResults) {
          this.loadComparisonData()
        }
      }
    },
    
    getCarColor(index) {
      return this.carColors[index % this.carColors.length]
    },
    
    // ===== 指标获取和判断 =====
    getMetricValue(car, key) {
      switch(key) {
        case 'total_count':
          return car.stats.total_count || 0
        case 'positiveRate':
          return car.positiveRate + '%'
        case 'avg_score':
          return car.avgScore
        case 'positive_count':
          return car.stats.positive_count || 0
        case 'negative_count':
          return car.stats.negative_count || 0
        default:
          return '-'
      }
    },
    
    isWinner(metricKey, car, reverse = false) {
      const values = this.comparisonResults.map(c => {
        switch(metricKey) {
          case 'total_count':
            return c.stats.total_count || 0
          case 'positiveRate':
            return parseFloat(c.positiveRate)
          case 'avg_score':
            return parseFloat(c.avgScore)
          case 'positive_count':
            return c.stats.positive_count || 0
          case 'negative_count':
            return c.stats.negative_count || 0
          default:
            return 0
        }
      })
      
      const currentValue = values[this.comparisonResults.indexOf(car)]
      const bestValue = reverse ? Math.min(...values) : Math.max(...values)
      return currentValue === bestValue && currentValue !== 0
    },
    
    // ===== 数据预加载 =====
    async preloadData() {
      try {
        this.loadSeriesList()
        this.preloadAllCarNames()
      } catch (error) {
        console.error('预加载数据失败:', error)
      }
    },
    
    async loadSeriesList() {
      try {
        const facets = await dongchediApi.getFilterFacets({})
        this.seriesList = facets.seriesNames || []
        this.comparisonCars.forEach(car => {
          car.filteredSeries = [...this.seriesList]
        })
      } catch (error) {
        console.error('加载车系列表失败:', error)
      }
    },
    
    async preloadAllCarNames() {
      try {
        const facets = await dongchediApi.getFilterFacets({})
        this.allCarNames = facets.carNames || []
      } catch (error) {
        console.error('预加载车型失败:', error)
      }
    },
    
    // ===== 下拉框操作 =====
    hideDropdown(index, type) {
      setTimeout(() => {
        const car = this.comparisonCars[index]
        if (car) {
          if (type === 'series') {
            car.seriesDropdown = false
          } else {
            car.modelDropdown = false
          }
        }
      }, 200)
    },
    
    filterSeries(car) {
      const search = car.seriesSearch.toLowerCase()
      if (!this.seriesList || this.seriesList.length === 0) {
        return
      }
      car.filteredSeries = this.seriesList.filter(s => 
        s.value.toLowerCase().includes(search)
      )
    },
    
    async selectSeries(car, series) {
      car.series = series
      car.seriesSearch = series
      car.seriesDropdown = false
      car.model = ''
      car.modelSearch = ''
      
      if (series) {
        await this.loadModelsList(car, series)
      } else {
        car.modelsList = []
        car.filteredModels = []
      }
    },
    
    async loadModelsList(car, series) {
      try {
        const facets = await dongchediApi.getFilterFacets({ seriesName: series })
        car.modelsList = facets.carNames || []
        car.filteredModels = [...car.modelsList]
      } catch (error) {
        console.error('加载车型列表失败:', error)
      }
    },
    
    filterModels(car) {
      const search = car.modelSearch.toLowerCase()
      car.filteredModels = car.modelsList.filter(m => 
        m.value.toLowerCase().includes(search)
      )
    },
    
    selectModel(car, model) {
      car.model = model
      car.modelSearch = model
      car.modelDropdown = false
    },
    
    // ===== 对比数据加载 =====
    async loadComparisonData() {
      if (!this.canCompare) return
      
      this.showResults = false
      this.comparisonResults = []
      
      const validCars = this.comparisonCars.filter(car => car.series || car.model)
      
      try {
        const promises = validCars.map(car => this.loadCarData(car))
        this.comparisonResults = await Promise.all(promises)
        
        this.showResults = true
        
        await this.$nextTick()
        this.renderAllCharts()
        
        await this.$nextTick()
        this.triggerAIAnalysis()
      } catch (error) {
        console.error('加载对比数据失败:', error)
        alert('加载对比数据失败，请重试')
      }
    },
    
    async loadCarData(car) {
      try {
        const filters = {}
        if (car.model) filters.carName = car.model
        else if (car.series) filters.seriesName = car.series

        const [stats, sentimentDist, topicData] = await Promise.all([
          dongchediApi.getSentimentStats(filters),
          dongchediApi.getSentimentDistribution(filters),
          dongchediApi.getTopicSentimentDistribution(filters)
        ])
        
        const advantages = topicData
          .filter(t => t.sentiment === 'positive')
          .sort((a, b) => parseInt(b.count) - parseInt(a.count))
          .slice(0, 5)
          .map(t => ({ aspect: t.aspect, count: parseInt(t.count) }))
        
        const disadvantages = topicData
          .filter(t => t.sentiment === 'negative')
          .sort((a, b) => parseInt(b.count) - parseInt(a.count))
          .slice(0, 5)
          .map(t => ({ aspect: t.aspect, count: parseInt(t.count) }))
        
        const positiveRate = stats.total_count > 0
          ? ((stats.positive_count / stats.total_count) * 100).toFixed(1)
          : '0.0'
        const avgScore = parseFloat(stats.avg_score || 0).toFixed(2)
        
        return {
          id: car.id,
          series: car.series,
          model: car.model,
          displayName: car.model || car.series,
          stats,
          positiveRate,
          avgScore,
          sentimentDist,
          topicData,
          advantages,
          disadvantages
        }
      } catch (error) {
        console.error('加载车型数据失败:', car, error)
        throw error
      }
    },
    
    // ===== 图表渲染 =====
    renderAllCharts() {
      this.comparisonResults.forEach((car, index) => {
        this.renderSentimentChart(car, index)
        this.renderTopicChart(car, index)
      })
    },
    
    renderSentimentChart(car, index) {
      const container = this.chartRefs.sentiment[car.id]
      if (!container) return
      
      const chart = echarts.init(container)
      this.chartInstances.sentiment[car.id] = chart
      
      const sentimentMap = { positive: '正面', negative: '负面', neutral: '中性' }
      const colors = { '正面': '#48bb78', '负面': '#f56565', '中性': '#a0aec0' }

      const chartData = car.sentimentDist.map(item => ({
        name: sentimentMap[item.sentiment] || item.sentiment,
        value: parseInt(item.count),
        itemStyle: { color: colors[sentimentMap[item.sentiment]] }
      }))
      
      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: 0,
          left: 'center'
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: chartData
        }]
      })
    },
    
    renderTopicChart(car, index) {
      const container = this.chartRefs.topic[car.id]
      if (!container) return
      
      const chart = echarts.init(container)
      this.chartInstances.topic[car.id] = chart
      
      const aspects = [...new Set(car.topicData.map(item => item.aspect))].slice(0, 10)
      const sentiments = ['positive', 'negative', 'neutral']
      const sentimentMap = { positive: '正面', negative: '负面', neutral: '中性' }
      const colors = { positive: '#48bb78', negative: '#f56565', neutral: '#a0aec0' }

      const series = sentiments.map(sentiment => ({
        name: sentimentMap[sentiment],
        type: 'bar',
        stack: 'total',
        data: aspects.map(aspect => {
          const found = car.topicData.find(d => d.aspect === aspect && d.sentiment === sentiment)
          return found ? parseInt(found.count) : 0
        }),
        itemStyle: { color: colors[sentiment] }
      }))
      
      chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: Object.values(sentimentMap),
          top: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: aspects
        },
        series: series
      })
    },
    
    destroyAllCharts() {
      Object.values(this.chartInstances.sentiment).forEach(chart => chart && chart.dispose())
      Object.values(this.chartInstances.topic).forEach(chart => chart && chart.dispose())
      this.chartInstances.sentiment = {}
      this.chartInstances.topic = {}
    },
    
    goBack() {
      this.$router.push('/')
    },

    // ===== AI分析 =====
    async triggerAIAnalysis() {
      this.showAIAnalysis = true
      this.aiAnalyzing = true
      this.aiAnalysisText = ''
      this.displayedText = ''
      this.renderedHtml = ''
      this.aiStreaming = true

      try {
        // 获取当前用户角色信息
        const userRole = localStorage.getItem('userRole') || '普通用户'
        const userRoleValue = localStorage.getItem('userRoleValue') || 'normal_user'

        // 为每辆车收集更丰富的证据（原话、趋势、伴随关系）
        const quotesByCar = {}
        const trendByCar = {}
        const cooccurrenceByCar = {}
        for (const car of this.comparisonResults) {
          const filters = {}
          if (car.model) filters.carName = car.model
          else if (car.series) filters.seriesName = car.series

          const topNegAspects = (car.disadvantages || []).slice(0, 2).map(x => x.aspect)
          const topPosAspects = (car.advantages || []).slice(0, 2).map(x => x.aspect)
          const aspects = Array.from(new Set([ ...topNegAspects, ...topPosAspects ].filter(Boolean)))

          const quotesByAspect = {}
          for (const asp of aspects) {
            try {
              const rows = await dongchediApi.getAspectAnalysis({ ...filters, keyword: asp })
              const pos = rows.filter(r => r.sentiment === 'positive').slice(0, 3).map(r => r.original_sentence)
              const neg = rows.filter(r => r.sentiment === 'negative').slice(0, 3).map(r => r.original_sentence)
              quotesByAspect[asp] = { positive: pos, negative: neg }
            } catch (e) {
              // 单个方面证据获取失败不影响整体
            }
          }
          quotesByCar[car.displayName] = quotesByAspect

          // 趋势：该车最近30天 vs 前30天的正面率与平均分
          try {
            const end = new Date()
            const toISO = (d) => new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().slice(0, 19).replace('T', ' ')
            const recentStart = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000)
            const prevStart = new Date(recentStart.getTime() - 30 * 24 * 60 * 60 * 1000)
            const statsRecent = await dongchediApi.getSentimentStats({ ...filters, startDate: toISO(recentStart), endDate: toISO(end) })
            const statsPrev = await dongchediApi.getSentimentStats({ ...filters, startDate: toISO(prevStart), endDate: toISO(recentStart) })
            const safeRate = (s) => {
              const total = parseInt(s.total_count || 0)
              const pos = parseInt(s.positive_count || 0)
              return total > 0 ? (pos / total * 100).toFixed(1) : '0.0'
            }
            trendByCar[car.displayName] = {
              recent: { positiveRate: safeRate(statsRecent), avgScore: parseFloat(statsRecent.avg_score || 0).toFixed(2) },
              previous: { positiveRate: safeRate(statsPrev), avgScore: parseFloat(statsPrev.avg_score || 0).toFixed(2) }
            }
          } catch (e) {}

          // 伴随关系：近60天内该车原话中的方面共现
          try {
            const end = new Date()
            const sixtyStart = new Date(end.getTime() - 60 * 24 * 60 * 60 * 1000)
            const toISO = (d) => new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().slice(0, 19).replace('T', ' ')
            const rows = await dongchediApi.getAspectAnalysis({ ...filters, startDate: toISO(sixtyStart), endDate: toISO(end) })
            const pairCount = {}
            rows.forEach(r => {
              try {
                const arr = JSON.parse(r.opinion_analysis || '[]')
                const aspects = (arr || []).map(x => x.aspect).filter(Boolean)
                for (let i = 0; i < aspects.length; i++) {
                  for (let j = i + 1; j < aspects.length; j++) {
                    const a = aspects[i]
                    const b = aspects[j]
                    const key = a < b ? `${a}__${b}` : `${b}__${a}`
                    pairCount[key] = (pairCount[key] || 0) + 1
                  }
                }
              } catch (e) {}
            })
            cooccurrenceByCar[car.displayName] = Object.entries(pairCount)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([key, cnt]) => {
                const [a, b] = key.split('__')
                return { pair: [a, b], count: cnt }
              })
          } catch (e) {}
        }
        const evidence = { quotesByCar, trendByCar, cooccurrenceByCar }
        
        const prompt = this.generateMultiComparisonPrompt(userRole, userRoleValue, evidence)
        
        await aiApi.analyzeData(prompt, (chunk, fullText) => {
          this.aiAnalyzing = false
          this.aiAnalysisText = fullText
          this.displayedText = fullText
          this.renderedHtml = renderMarkdown(fullText)
        })
        
        this.aiStreaming = false
      } catch (error) {
        console.error('AI分析失败:', error)
        this.aiAnalyzing = false
        this.aiStreaming = false
        this.aiAnalysisText = '抱歉，AI分析暂时不可用，请稍后再试。'
        this.displayedText = this.aiAnalysisText
      }
    },

    generateMultiComparisonPrompt(userRole, userRoleValue, evidence = null) {
      const carNames = this.comparisonResults.map(c => c.displayName).join('、')
      const carCount = this.comparisonResults.length
      
      // 根据角色获取角色目标描述
      const getRoleDescription = (roleValue) => {
        const roleDescriptions = {
          'normal_user': '关注产品使用体验和口碑评价',
          'product_manager': '关注产品功能和用户体验',
          'marketing': '关注品牌形象和市场反馈',
          'data_analyst': '关注数据洞察和趋势分析',
          'developer': '关注技术实现和性能优化',
          'executive': '关注整体业务和战略决策'
        }
        return roleDescriptions[roleValue] || '关注产品使用体验和口碑评价'
      }
      
      let prompt = `# 汽车口碑多车型对比分析任务

## 背景信息
你是一位专业的汽车行业分析师，正在为${userRole}角色提供分析服务。该角色的主要关注点是：${getRoleDescription(userRoleValue)}。
本次对比的车型包括：${carNames}，共${carCount}款。

## 分析数据

`
      
      this.comparisonResults.forEach((car, index) => {
        prompt += `
### ${String.fromCharCode(65 + index)}. ${car.displayName}

**核心指标：**
- 评论总数：${car.stats.total_count || 0}
- 正面率：${car.positiveRate}%
- 平均情感分：${car.avgScore}
- 正面评论：${car.stats.positive_count || 0}
- 负面评论：${car.stats.negative_count || 0}

**优势（Top 5）：**
${car.advantages.map(a => `- ${a.aspect}（${a.count}次提及）`).join('\n')}

**劣势（Top 5）：**
${car.disadvantages.map(d => `- ${d.aspect}（${d.count}次提及）`).join('\n')}

`
      })

      prompt += `
## 分析要求

请基于以上数据，从以下维度进行专业的对比分析，并特别关注${userRole}角色的需求：

1. **综合实力排名**：根据核心指标给出综合排名，说明排名依据
2. **各车型特点**：用一句话概括每款车的最大特点
3. **优势对比**：横向对比各车型的突出优势
4. **劣势对比**：指出各车型需要改进的方面
5. **购买建议**：针对${userRole}角色的需求，给出具体的选购建议

## 证据与趋势（供你引用）
${evidence ? Object.entries(evidence.quotesByCar).map(([name, aspects]) => {
  const lines = []
  Object.entries(aspects).forEach(([asp, q]) => {
    const pos = (q.positive || []).map(s => `『${s}』`).join('\n')
    const neg = (q.negative || []).map(s => `『${s}』`).join('\n')
    lines.push(`- ${asp}\n  正面:\n  ${pos || '（无）'}\n  负面:\n  ${neg || '（无）'}`)
  })
  const trend = evidence.trendByCar && evidence.trendByCar[name] ? evidence.trendByCar[name] : null
  const co = evidence.cooccurrenceByCar && evidence.cooccurrenceByCar[name] ? evidence.cooccurrenceByCar[name] : []
  const trendText = trend ? `\n- 趋势：最近30天 正面率 ${trend.recent.positiveRate}% vs 前30天 ${trend.previous.positiveRate}%；平均分 ${trend.recent.avgScore} vs ${trend.previous.avgScore}` : ''
  const coText = co.length ? `\n- 伴随关系：${co.map(p => `${p.pair[0]}↔${p.pair[1]}(${p.count})`).join('，')}` : ''
  return `### ${name}${trendText}${coText}\n${lines.join('\n')}`
}).join('\n\n') : '（证据收集中，若为空请仅根据数据分析）'}

## 输出要求
- 使用简洁专业的语言
- 先给出一行“综合胜出与理由”，随后展开洞察
- 每个关键结论引用至少1条用户原话（使用中文引号『』标注）
- 明确1个“伴随关系”解释差异的可能因果链
- 给出可执行建议（明确“谁/何时/做什么/影响哪个指标”），贴合${userRole}诉求
- 字数控制在350-600字
`

      return prompt
    },
    
    closeAIAnalysis() {
      this.showAIAnalysis = false
      this.aiIconMinimized = true
    },
    
    openAIAnalysis() {
      if (this.aiAnalysisText) {
        this.showAIAnalysis = true
        this.aiIconMinimized = false
      }
    },
    
    // ===== 快捷助手 =====
    initKeyboardShortcut() {
      window.addEventListener('keydown', this.handleKeyDown)
    },
    
    handleKeyDown(e) {
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') {
        e.preventDefault()
        clearTimeout(this.spaceTimer)
        this.spaceCount++
        if (this.spaceCount === 2) {
          this.toggleQuickPanel()
          this.spaceCount = 0
        }
        this.spaceTimer = setTimeout(() => {
          this.spaceCount = 0
        }, 500)
      }
    },
    
    toggleQuickPanel() {
      if (!this.showQuickPanel) {
        if (this.showAIAnalysis) {
          this.showAIAnalysis = false
          this.aiIconMinimized = true
        }
      }
      this.showQuickPanel = !this.showQuickPanel
    },

    // ===== 角色选择器 =====
    handleRoleChanged(role) {
      this.currentRole = role
      console.log('当前角色:', this.currentRole)
      // 根据角色执行不同的操作，例如重新加载数据或更新界面
      // 例如，如果角色是分析师，则重新加载数据
      if (this.currentRole === '分析师') {
        this.loadComparisonData()
      }
    },

    logout() {
      localStorage.removeItem('username')
      localStorage.removeItem('userRole')
      this.$router.push('/login')
      alert('已退出登录')
    }
  }
}
</script>

<style scoped>
.comparison-page {
  --brand-start: #5b6df2;
  --brand-end: #7b5cff;
  --success: #22c55e;
  --danger: #ef4444;
  --warning: #f59e0b;
  background: linear-gradient(135deg, #f0f4ff 0%, #fef3f2 100%);
  min-height: 100vh;
  padding: 20px;
}

/* ===== 头部 ===== */
.header {
  text-align: center;
  margin-bottom: 30px;
  padding: 30px;
  background: white;
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  position: relative;
}

.btn-back {
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  padding: 10px 20px;
  background: #f7fafc;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  color: #4a5568;
}

.btn-back:hover {
  background: #edf2f7;
  transform: translateY(-50%) translateX(-3px);
}

.header .user-info {
  position: absolute;
  top: 20px;
  right: 30px;
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f7fafc;
  border-radius: 20px;
  padding: 8px 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.header .username {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.header .btn-logout {
  padding: 8px 12px;
  background: #f56565;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
}

.header .btn-logout:hover {
  background: #ef4444;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(239,68,68,0.3);
}

.header h1 {
  font-size: 36px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--brand-start) 0%, var(--brand-end) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}

.header p {
  color: #718096;
  font-size: 15px;
  font-weight: 500;
}

/* ===== 选择器区域 - 全新极简设计 ===== */
.selector-wrapper {
  background: white;
  border-radius: 24px;
  padding: 35px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  margin-bottom: 35px;
}

.selector-container {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 25px;
}

.selector-container.cars-2,
.selector-container.cars-3 {
  justify-content: center;
}

.car-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px 20px;
  transition: all 0.3s;
  position: relative;
  flex: 0 0 auto;
}

.car-selector:hover {
  background: #f1f5f9;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.car-label {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  font-weight: 900;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.car-inputs {
  display: flex;
  gap: 10px;
}

.input-wrapper {
  min-width: 160px;
}

.searchable-select {
  position: relative;
  width: 100%;
}

.select-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  background: white;
  box-sizing: border-box;
  color: #2d3748;
}

.select-input:focus {
  outline: none;
  border-color: var(--brand-start);
  box-shadow: 0 0 0 3px rgba(91,109,242,0.08);
}

.select-input:disabled {
  background: #f7fafc;
  cursor: not-allowed;
  opacity: 0.5;
}

.select-input::placeholder {
  color: #a0aec0;
  font-weight: 400;
}

.dropdown-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid var(--brand-start);
  border-top: none;
  border-radius: 0 0 10px 10px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000000000;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  margin-top: -2px;
  list-style: none;
  padding: 0;
}

.dropdown-item {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  border-top: 1px solid #f0f0f0;
  color: #4a5568;
}

.dropdown-item:first-child {
  border-top: none;
  font-weight: 700;
  color: var(--brand-start);
}

.dropdown-item:hover {
  background: linear-gradient(90deg, rgba(91,109,242,0.1) 0%, rgba(91,109,242,0.05) 100%);
}

.btn-remove-car {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 28px;
  height: 28px;
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(239,68,68,0.3);
}

.btn-remove-car:hover {
  transform: rotate(90deg) scale(1.15);
  box-shadow: 0 4px 12px rgba(239,68,68,0.4);
}

.vs-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--brand-start) 0%, var(--brand-end) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 900;
  box-shadow: 0 4px 16px rgba(91,109,242,0.25);
  animation: pulse 2s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.btn-add-car {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: transparent;
  border: 2px dashed #cbd5e0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s;
  color: #718096;
  font-size: 14px;
  font-weight: 600;
}

.btn-add-car:hover {
  border-color: var(--brand-start);
  background: rgba(91,109,242,0.05);
  color: var(--brand-start);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(91,109,242,0.12);
}

.add-plus {
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
}

.add-label {
  font-size: 14px;
}

.btn-start-compare {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, var(--brand-start) 0%, var(--brand-end) 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(91,109,242,0.25);
  position: relative;
  overflow: hidden;
}

.btn-start-compare::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.6s;
}

.btn-start-compare:hover:not(:disabled)::before {
  left: 100%;
}

.btn-start-compare:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(91,109,242,0.35);
}

.btn-start-compare:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

/* ===== 对比结果 ===== */
.comparison-results {
  animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  color: #1a202c;
  margin: 50px 0 25px 0;
  padding: 18px 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  border-left: 6px solid var(--brand-start);
}

/* ===== 对称布局（2-3车） ===== */
.metrics-symmetric {
  background: white;
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin-bottom: 40px;
}

.metric-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  position: relative;
  align-items: stretch;
}

.metric-row:last-child {
  margin-bottom: 0;
}

.metric-cell {
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 16px;
  border-top: 4px solid;
  padding: 24px;
  text-align: center;
  transition: all 0.3s;
  position: relative;
}

.metric-cell:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}

.metric-cell.winner {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  transform: scale(1.05);
  box-shadow: 0 8px 24px rgba(251,191,36,0.3);
}

.metric-cell.winner::before {
  content: '👑';
  position: absolute;
  top: -12px;
  right: -12px;
  font-size: 28px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

.car-name {
  font-size: 14px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  font-size: 36px;
  font-weight: 900;
  color: #1a202c;
  margin-bottom: 8px;
  line-height: 1;
}

.metric-label {
  font-size: 13px;
  color: #718096;
  font-weight: 600;
}

.metric-name-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 12px 24px;
  border-radius: 30px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  font-size: 15px;
  font-weight: 700;
  color: #2d3748;
  white-space: nowrap;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
}

.metric-icon {
  font-size: 18px;
}

/* ===== 图表行（2-3车） ===== */
.charts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.chart-card {
  background: white;
  border-radius: 20px;
  border-top: 4px solid;
  padding: 25px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.chart-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f7fafc;
}

.car-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 800;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.chart-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  flex: 1;
}

.chart {
  width: 100%;
  height: 320px;
}

.chart-large {
  width: 100%;
  height: 400px;
}

/* ===== 优劣势行（2-3车） ===== */
.advantages-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.advantage-card {
  background: white;
  border-radius: 20px;
  border-top: 4px solid;
  padding: 28px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.advantage-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}

.advantage-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 25px;
  padding-bottom: 18px;
  border-bottom: 2px solid #f7fafc;
}

.advantage-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  flex: 1;
}

.advantage-section {
  margin-bottom: 25px;
}

.advantage-section:last-child {
  margin-bottom: 0;
}

.advantage-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 15px 0;
  font-size: 15px;
  font-weight: 700;
}

.advantage-title.positive-title {
  color: var(--success);
}

.advantage-title.negative-title {
  color: var(--danger);
}

.title-icon {
  font-size: 18px;
  font-weight: 900;
}

.title-count {
  font-size: 12px;
  color: #a0aec0;
  font-weight: 600;
  margin-left: auto;
}

.advantage-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.advantage-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  transition: all 0.3s;
}

.advantage-item.positive {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.advantage-item.negative {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.advantage-item:hover {
  transform: translateX(6px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.item-rank {
  width: 26px;
  height: 26px;
  background: rgba(255,255,255,0.7);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  color: #2d3748;
}

.item-text {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.item-count {
  font-size: 13px;
  font-weight: 700;
  color: #718096;
  background: rgba(255,255,255,0.6);
  padding: 4px 10px;
  border-radius: 12px;
}

.empty-hint {
  text-align: center;
  color: #a0aec0;
  padding: 20px;
  font-size: 14px;
  font-weight: 500;
  font-style: italic;
}

/* ===== 表格布局（4-5车） ===== */
.metrics-table-wrapper {
  overflow-x: auto;
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin-bottom: 40px;
}

.metrics-table {
  width: 100%;
  border-collapse: collapse;
}

.metrics-table thead th {
  padding: 18px 16px;
  font-weight: 800;
  font-size: 15px;
  text-align: center;
  border: 2px solid white;
  color: white;
}

.metrics-table thead th.metric-name-col {
  background: #1a202c;
  text-align: left;
}

.metrics-table tbody td {
  padding: 18px 16px;
  text-align: center;
  border: 1px solid #e2e8f0;
  transition: all 0.3s;
}

.metrics-table tbody td.metric-name-col {
  font-weight: 700;
  background: #f7fafc;
  text-align: left;
  font-size: 14px;
  color: #2d3748;
}

.metrics-table tbody td.best {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(34, 197, 94, 0.25) 100%);
  font-weight: 800;
  position: relative;
}

.metrics-table tbody td.best::after {
  content: '🏆';
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 18px;
}

.metrics-table .metric-value {
  font-size: 20px;
  font-weight: 800;
  color: #1a202c;
}

/* ===== 网格布局（4-5车） ===== */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

/* ===== 空状态 ===== */
.empty-state {
  text-align: center;
  padding: 100px 20px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.empty-icon {
  font-size: 100px;
  margin-bottom: 20px;
  opacity: 0.4;
}

.empty-text {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
}

.empty-hint {
  font-size: 15px;
  color: #a0aec0;
  font-weight: 500;
}

/* ===== AI相关样式 ===== */
.ai-analysis-popup {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 340px;
  max-width: calc(100vw - 60px);
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  z-index: 10000;
  overflow: hidden;
  animation: popup-bounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes popup-bounce {
  0% { transform: scale(0.8) translateY(20px); opacity: 0; }
  50% { transform: scale(1.05) translateY(-5px); }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}

.ai-popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, var(--brand-start) 0%, var(--brand-end) 100%);
  color: white;
}

.ai-popup-title {
  font-size: 15px;
  font-weight: 700;
}

.ai-popup-close {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-popup-close:hover {
  background: rgba(255,255,255,0.3);
  transform: rotate(90deg);
}

.ai-popup-body {
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.ai-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
}

.ai-loading-dots {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.ai-loading-dots span {
  width: 12px;
  height: 12px;
  background: var(--brand-start);
  border-radius: 50%;
  animation: dot-bounce 1.4s ease-in-out infinite;
}

.ai-loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.ai-loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1.2); opacity: 1; }
}

.ai-loading p {
  color: #718096;
  font-size: 14px;
  font-weight: 500;
}

.ai-content {
  font-size: 14px;
  line-height: 1.8;
  color: #2d3748;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Markdown 内容样式（窄弹窗适配；保守配色） */
.md-content { font-size: 14px; line-height: 1.75; color: #2d3748; }
.md-content h1, .md-content h2, .md-content h3 { margin: 6px 0 4px; font-weight: 700; }
.md-content h1 { font-size: 16px; }
.md-content h2 { font-size: 15px; }
.md-content h3 { font-size: 14px; }
.md-content p { margin: 6px 0; }
.md-content ul, .md-content ol { padding-left: 18px; margin: 6px 0; }
.md-content code { background: #f4f6fb; border-radius: 4px; padding: 0 4px; font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; }
.md-content pre code { display: block; padding: 10px; overflow-x: auto; }
.md-content blockquote { margin: 8px 0; padding: 6px 10px; border-left: 3px solid #a5b4fc; background: #f8f9ff; color: #334155; }
.md-content table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
.md-content thead th { background: #eef2ff; color: #334155; font-weight: 700; }
.md-content th, .md-content td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
.md-content tbody tr:nth-child(odd) { background: #fafafa; }
.md-content a { color: #667eea; text-decoration: none; }
.md-content a:hover { text-decoration: underline; }

.cursor-blink {
  display: inline-block;
  animation: blink 0.7s step-end infinite;
  color: var(--brand-start);
  font-weight: bold;
  margin-left: 2px;
}

@keyframes blink {
  50% { opacity: 0; }
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.4s ease;
}

.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.ai-icon-minimized {
  position: fixed;
  bottom: 80px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--brand-start) 0%, var(--brand-end) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(91,109,242,0.4);
  z-index: 9998;
  transition: all 0.3s;
}

.ai-icon-minimized:hover {
  transform: scale(1.1);
}

.ai-icon-minimized .icon {
  font-size: 30px;
  z-index: 2;
  position: relative;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid rgba(91,109,242,0.6);
  border-radius: 50%;
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.assistant-hint {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, rgba(91,109,242,0.95) 0%, rgba(123,92,255,0.95) 100%);
  color: white;
  padding: 14px 28px;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(91,109,242,0.3);
  cursor: pointer;
  z-index: 8888;
  transition: all 0.3s;
}

.assistant-hint:hover {
  transform: translateX(-50%) scale(1.05);
}

.hint-icon {
  margin-right: 8px;
}

.kbd {
  padding: 3px 8px;
  background: rgba(255,255,255,0.25);
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
  font-weight: 700;
}

.quick-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.quick-panel {
  width: 55vw;
  height: 85vh;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.quick-panel iframe {
  width: 100%;
  height: 100%;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: all 0.35s cubic-bezier(.22,.61,.36,1);
}

.slide-up-enter {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

/* ===== 响应式 ===== */
@media (max-width: 1200px) {
  .charts-row,
  .advantages-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header h1 {
    font-size: 28px;
  }
  
  .selector-wrapper {
    padding: 25px 20px;
  }
  
  .selector-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .car-selector {
    flex-direction: column;
    align-items: stretch;
  }
  
  .car-inputs {
    flex-direction: column;
  }
  
  .input-wrapper {
    min-width: 100%;
  }
  
  .vs-icon {
    align-self: center;
  }
  
  .section-title {
    font-size: 20px;
    padding: 15px 20px;
  }
  
  .metric-value {
    font-size: 28px;
  }
  
  .chart,
  .chart-large {
    height: 280px;
  }
}
</style>
